import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Container,
  Alert
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { sendVerificationCode, login } from '../../Api/index.ts';
import { Link } from 'react-router-dom';
import VerifyIdentity from '../../Components/VerifyIdentity/VerifyIdentity';

export default function Login() {
  const navigate = useNavigate();
  const [step, setStep] = useState('email'); // 'email' or 'verification'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSendCode = async () => {
    if (!email.trim()) {
      setErrorMessage('Email is required');
      return;
    }

    setLoading(true);
    setErrorMessage('');

    try {
      console.log('Sending formData to backend:', { email: email.trim() });
      const response = await sendVerificationCode({
        email: email.trim(),
        first_name: '', // Not needed for login
        last_name: '', // Not needed for login
        school: '' // Not needed for login
      });
      
      if (response.error) {
        setErrorMessage(response.error);
      } else {
        setStep('verification');
        // setSuccessMessage('Verification code sent successfully!'); // This line was removed
      }
    } catch (error) {
      setErrorMessage('Failed to send verification code. Please try again.');
      console.error('Error sending verification code:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    if (!code.trim()) {
      setErrorMessage('Verification code is required');
      return;
    }

    setLoading(true);
    setErrorMessage('');

    try {
      const response = await login({
        email: email.trim(),
        code: code.trim()
      });
      
      if (response.error) {
        setErrorMessage(response.error);
      } else {
        // setSuccessMessage('Login successful! Redirecting...'); // This line was removed
        setTimeout(() => {
          navigate('/dashboard');
        }, 1000);
      }
    } catch (error) {
      setErrorMessage('Failed to login. Please check your code and try again.');
      console.error('Error logging in:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToEmail = () => {
    setStep('email');
    setCode('');
    setErrorMessage('');
    // setSuccessMessage(''); // This line was removed
  };

  const handleVerify = () => {
    handleLogin();
  };

  const handleResend = () => {
    handleSendCode();
  };

  if (step === 'verification') {
    return (
      <VerifyIdentity
        email={email}
        formData={{ email: email, first_name: '', last_name: '', school: '' }}
        onVerify={handleVerify}
        onResend={handleResend}
        onGoBack={handleBackToEmail}
        title="Verify Your Identity"
      />
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 8, display: 'flex' }}>
      {/* Left Side - Hidden on mobile */}
      <Box sx={{ 
        flex: 1, 
        pr: 8,
        display: { xs: 'none', md: 'block' }
      }}>
        <Typography variant="h3" sx={{ 
          fontWeight: 'bold', 
          mb: 4, 
          fontSize: { xs: '2rem', md: '2.5rem' },
          color: '#000000'
        }}>
          Buy, Sell Textbooks with Ease—Connecting Students for a More Affordable Education!
        </Typography>

        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 3 }}>
          <Box sx={{ 
            color: '#6B89FF', 
            fontSize: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            backgroundColor: 'rgba(107, 137, 255, 0.1)',
            boxShadow: '0 2px 8px rgba(107, 137, 255, 0.2)'
          }}>✓</Box>
          <Typography variant="h6" sx={{ 
            fontSize: { xs: '1.1rem', md: '1.3rem' },
            fontWeight: '500',
            color: '#2c3e50'
          }}>
            10,000+ textbooks available for exchange
          </Typography>
        </Box>

        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 3 }}>
          <Box sx={{ 
            color: '#6B89FF', 
            fontSize: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            backgroundColor: 'rgba(107, 137, 255, 0.1)',
            boxShadow: '0 2px 8px rgba(107, 137, 255, 0.2)'
          }}>✓</Box>
          <Typography variant="h6" sx={{ 
            fontSize: { xs: '1.1rem', md: '1.3rem' },
            fontWeight: '500',
            color: '#2c3e50'
          }}>
            Buy and sell textbooks directly with fellow students
          </Typography>
        </Box>

        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 3 }}>
          <Box sx={{ 
            color: '#6B89FF', 
            fontSize: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            backgroundColor: 'rgba(107, 137, 255, 0.1)',
            boxShadow: '0 2px 8px rgba(107, 137, 255, 0.2)'
          }}>✓</Box>
          <Typography variant="h6" sx={{ 
            fontSize: { xs: '1.1rem', md: '1.3rem' },
            fontWeight: '500',
            color: '#2c3e50'
          }}>
            Get the books you need for less—no middleman, no hassle
          </Typography>
        </Box>
      </Box>

      {/* Right Side - Full width on mobile */}
      <Box sx={{ 
        flex: 1,
        width: { xs: '100%', md: 'auto' }
      }}>
        <Typography variant="h4" sx={{ 
          mb: 1, 
          fontWeight: 'bold', 
          textAlign: 'center',
          color: '#000000'
        }}>
          Sign In
        </Typography>
        <Typography sx={{ 
          mb: 4, 
          textAlign: 'center',
          color: '#666',
          fontSize: '1.1rem'
        }}>
          to your bookloop account
        </Typography>

        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}

        <Box component="form" sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: 3,
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          padding: 3,
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(107, 137, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(107, 137, 255, 0.1)'
        }}>
          <Box sx={{ height: '56px' }}>
            <TextField
              fullWidth
              label="University Email"
              variant="outlined"
              type="email"
              placeholder="yourname@university.ca"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={!!errorMessage}
              helperText={errorMessage || "Enter your university email address"}
              sx={{
                '& .MuiFormHelperText-root': {
                  position: 'absolute',
                  bottom: '-20px',
                }
              }}
            />
          </Box>

          <Button
            variant="contained"
            sx={{
              mt: 3,
              background: 'linear-gradient(135deg, #6B89FF 0%, #5A75E6 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5A75E6 0%, #4A65D6 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(107, 137, 255, 0.3)'
              },
              textTransform: 'none',
              borderRadius: '30px',
              height: '56px',
              fontSize: '18px',
              fontWeight: '600',
              width: '250px',
              display: 'block',
              mx: 'auto',
              boxShadow: '0 4px 15px rgba(107, 137, 255, 0.2)',
              transition: 'all 0.3s ease',
              '&:disabled': {
                backgroundColor: '#ccc',
                transform: 'none',
                boxShadow: 'none'
              }
            }}
            onClick={handleSendCode}
            disabled={loading}
          >
            {loading ? 'Sending Code...' : 'Send Verification Code'}
          </Button>

          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography sx={{ mb: 1 }}>
              Don't have an account? <Link to="/create-account" style={{ color: '#6B89FF', textDecoration: 'none' }}>Create one</Link>
            </Typography>
          </Box>
        </Box>
      </Box>
    </Container>
  );
}